import * as R from 'ramda';
import { connect } from 'react-redux';
import React, { useMemo } from 'react';
import { pure, compose, lifecycle, withHandlers } from 'react-recompose';
// components
import Resizable from '../resizable';
// hocs
import { withDraggableHOC } from '../../hocs';
// helpers/constants
import * as G from '../../helpers';
// icons
import * as I from '../../svgs';
import * as GC from '../../constants';
// ui
import {
  Box,
  FixedBox,
  AbsoluteBox,
  FlexHovered,
  ModalCancelButton,
  ModalActionButton,
} from '../../ui';
// components common/modal
import { closeModal } from './actions';
import {
  Title,
  Footer,
  Header,
  ModalCard,
  modalStyles,
  isFullHeight,
  ModalWrapper,
  ModalContentWrapper,
} from './ui';
//////////////////////////////////////////////////

const comomonProps = ['height', 'overflow', 'backgroundColor'];
const sizeProps = ['width', 'height', 'maxWidth', 'minWidth', 'minHeight', 'maxHeight'];

const getContentRadii = (withHeader) => ({
  borderBottomLeftRadius: 'inherit',
  borderBottomRightRadius: 'inherit',
  borderTopLeftRadius: G.ifElse(withHeader, '0px', 'inherit'),
  borderTopRightRadius: G.ifElse(withHeader, '0px', 'inherit'),
});

const CloseIconButton = ({ color, onClick, boxShadow, size = 28, bg = 'transparent' }) => (
  <FlexHovered
    bg={bg}
    width={size}
    height={size}
    cursor='pointer'
    onClick={onClick}
    borderRadius='50%'
    boxShadow={boxShadow}
    justifyContent='center'
    bgColor={G.getTheme('colors.#F6F6F6')}
    transition='background-color .15s ease'
    onMouseDown={(event) => event.stopPropagation()}
  >
    {I.closeIcon(R.or(color, modalStyles.subTextColor), 12)}
  </FlexHovered>
);

const CloseControls = (props) => {
  const { title, withCloseIcon, closeIconColor, handleCloseModal } = props;

  if (R.not(R.and(withCloseIcon, G.isNilOrEmpty(title)))) return null;

  return (
    <AbsoluteBox top={0} right={0} zIndex={14} data-testid='modal-close-controls'>
      <CloseIconButton
        color={closeIconColor}
        bg={modalStyles.bgColor}
        onClick={handleCloseModal}
        boxShadow='0 2px 8px rgba(0, 0, 0, 0.2)'
      />
    </AbsoluteBox>
  );
};

const enhance = compose(
  withHandlers({
    handleCloseModal: ({ closeModal }) => ({ keyCode }) => {
      if (R.equals(keyCode, GC.EVENT_KEY_CODE_ESC)) closeModal();
    },
  }),
  lifecycle({
    componentDidMount() {
      document.addEventListener('keyup', this.props.handleCloseModal);
    },
    componentWillUnmount() {
      document.removeEventListener('keyup', this.props.handleCloseModal);
    },
  }),
  pure,
);

const HeaderComponent = (props) => {
  const {
    title,
    movable,
    closeIcon,
    closeAction,
    closeIconColor,
    handleMouseDown,
  } = props;

  const draggableProps = useMemo(() => G.ifElse(
    movable,
    {
      cursor: 'move',
      userSelect: 'none',
      onMouseDown: handleMouseDown,
    }, {},
  ), [movable]);

  return (
    <Header {...draggableProps}>
      <Title>{title}</Title>
      {
        G.isTrue(closeIcon) &&
        <CloseIconButton onClick={closeAction} color={closeIconColor} />
      }
    </Header>
  );
};

const FooterComponent = (props) => {
  const {
    buttons,
    btnWidth,
    footerJC,
    closeAction,
    footerBottom,
    footerZIndex,
    footerPadding,
    footerPosition,
  } = props;

  const getBtnMargin = (margin) => {
    if (R.or(G.isNilOrEmpty(footerJC), R.equals(footerJC, 'space-between'))) {
      return '0px';
    }

    return margin;
  };

  return (
    <Footer
      p={footerPadding}
      zIndex={footerZIndex}
      bottom={footerBottom}
      position={footerPosition}
      justifyContent={footerJC}
    >
      <ModalCancelButton
        zIndex={footerZIndex}
        width={R.or(btnWidth, 120)}
        onClick={(event) => closeAction(event)}
      >
        {G.getWindowLocale('actions:cancel', 'Cancel')}
      </ModalCancelButton>
      {
        buttons.map(({ id, name, margin, action }, i) => (
          <ModalActionButton
            key={i}
            form={id}
            type='submit'
            onClick={action}
            zIndex={footerZIndex}
            m={getBtnMargin(margin)}
            width={R.or(btnWidth, 120)}
          >
            {name}
          </ModalActionButton>
        ))
      }
    </Footer>
  );
};

const Content = (props) => {
  const {
    index,
    params,
    closeModal,
    isDraggable,
    handleMouseDown,
  } = props;

  const {
    p,
    options,
    component,
    TitleComponent,
  } = R.pathOr({}, ['modal'], params);

  const {
    title,
    footerJC,
    btnWidth,
    closeAction,
    footerZIndex,
    footerBottom,
    footerPadding,
    withCloseIcon,
    footerPosition,
    controlButtons,
    closeIconColor,
    movable = true,
    overflow = 'initial',
    componentWrapperHeight,
  } = R.pathOr({}, ['modal', 'options'], params);

  const closingModal = (params) => {
    if (G.isNotNil(closeAction)) closeAction(params);

    closeModal();
  };

  const modalOptions = R.or(options, {});

  const withDragBar = G.isAllTrue(
    isDraggable,
    G.isNilOrEmpty(title),
    R.not(isFullHeight(modalOptions)),
  );

  const modalParams = R.mergeRight(R.pick(comomonProps, modalOptions), { withDragBar });

  const withHeader = R.or(G.isNotNilAndNotEmpty(title), G.isNotNilAndNotEmpty(TitleComponent));

  return (
    <ModalContentWrapper
      zIndex={1}
      index={index}
      params={modalParams}
      isDraggable={isDraggable}
      {...R.pick(sizeProps, options)}
    >
      {
        withDragBar &&
        <AbsoluteBox
          top='0px'
          left='0px'
          right='0px'
          cursor='move'
          userSelect='none'
          alignItems='center'
          justifyContent='center'
          data-testid='modal-drag-bar'
          onMouseDown={handleMouseDown}
          height={modalStyles.dragBarHeight}
        >
          {I.moveIcon(G.getTheme('colors.#7D828C'), 16, 16)}
        </AbsoluteBox>
      }
      <ModalCard params={modalParams}>
        {
          G.isNotNilAndNotEmpty(TitleComponent) &&
          <TitleComponent />
        }
        {
          G.isNotNilAndNotEmpty(title) &&
          <HeaderComponent
            title={title}
            movable={movable}
            closeIcon={withCloseIcon}
            closeAction={closingModal}
            closeIconColor={closeIconColor}
            handleMouseDown={handleMouseDown}
          />
        }
        <Box
          minHeight={0}
          flex='1 1 auto'
          overflow={overflow}
          height={componentWrapperHeight}
          p={R.or(p, '15px 15px 70px 15px')}
          {...getContentRadii(withHeader)}
        >
          {component}
          {
            G.isNotNilAndNotEmpty(controlButtons) &&
            <FooterComponent
              footerJC={footerJC}
              btnWidth={btnWidth}
              buttons={controlButtons}
              closeAction={closingModal}
              footerZIndex={footerZIndex}
              footerBottom={footerBottom}
              footerPadding={footerPadding}
              footerPosition={footerPosition}
            />
          }
        </Box>
      </ModalCard>
    </ModalContentWrapper>
  );
};

const ModalWithDraggable = withDraggableHOC((props) => {
  const {
    top,
    left,
    index,
    maxWidth,
    closeModal,
    initialWidth,
    initialHeight,
    withCloseIcon,
    closeIconColor,
    handleRegisterChild,
  } = props;

  const title = R.pathOr('', ['params', 'modal', 'options', 'title'], props);

  return (
    <ModalWrapper index={index} maxWidth={maxWidth}>
      <AbsoluteBox
        top={top}
        zIndex={12}
        left={left}
        width={initialWidth}
        // TODO: uncomment if draggable doesn't work
        height={initialHeight}
        ref={(ref) => handleRegisterChild(ref, 'movableContainer')}
      >
        <CloseControls
          title={title}
          handleCloseModal={closeModal}
          withCloseIcon={withCloseIcon}
          closeIconColor={closeIconColor}
        />
        <Content {...props} isDraggable={true} />
      </AbsoluteBox>
    </ModalWrapper>
  );
});


const Component = (props) => {
  const {
    index,
    params,
    maxWidth,
    closeModal,
  } = props;

  if (R.path(['modal', 'isExpandedContainer'], params)) {
    const {
      modal: {
        title,
        options,
        component,
        fixedWidth,
        wrapperStyles,
      },
    } = params;

    const hasTitle = G.isNotNilAndNotEmpty(title);
    const heightSubtract = G.ifElse(hasTitle, 30, 0);
    const widthSubtract = G.ifElse(fixedWidth, 0, 25);

    return (
      <FixedBox
        top='0'
        width='100%'
        height='100%'
        zIndex={R.add(index, 1300)}
        background={G.getTheme('reactModal.overlayBgColor')}
      >
        <Resizable
          options={options}
          wrapperStyles={wrapperStyles}
        >
          {hasTitle && <HeaderComponent title={title} />}
          <Box
            overflow='auto'
            ml={G.ifElse(fixedWidth, 0, 25)}
            width={`calc(100% - ${widthSubtract}px)`}
            height={`calc(100vh - ${heightSubtract}px)`}
          >
            {component}
          </Box>
        </Resizable>
      </FixedBox>
    );
  }

  const title = R.pathOr('', ['params', 'modal', 'options', 'title'], props);
  const withCloseIcon = R.pathOr(false, ['params', 'modal', 'options', 'withCloseIcon'], props);
  const closeIconColor = R.pathOr(modalStyles.subTextColor, ['params', 'modal', 'options', 'closeIconColor'], props);

  if (G.isFalse(R.path(['params', 'modal', 'options', 'movable'], props))) {
    return (
      <ModalWrapper index={index} maxWidth={maxWidth}>
        <AbsoluteBox
          width={R.path(['params', 'modal', 'options', 'width'], props)}
          height={R.path(['params', 'modal', 'options', 'height'], props)}
          top={R.pathOr('initial', ['params', 'modal', 'options', 'top'], props)}
          left={R.pathOr('initial', ['params', 'modal', 'options', 'left'], props)}
          right={R.pathOr('initial', ['params', 'modal', 'options', 'right'], props)}
        >
          <Content {...props} />
          <CloseControls
            title={title}
            handleCloseModal={closeModal}
            withCloseIcon={withCloseIcon}
            closeIconColor={closeIconColor}
          />
        </AbsoluteBox>
      </ModalWrapper>
    );
  }

  return (
    <ModalWithDraggable
      {...props}
      withCloseIcon={withCloseIcon}
      closeIconColor={closeIconColor}
      initialWidth={R.path(['params', 'modal', 'options', 'width'], props)}
      initialHeight={R.path(['params', 'modal', 'options', 'height'], props)}
      top={R.pathOr('initial', ['params', 'modal', 'options', 'top'], props)}
      left={R.pathOr('initial', ['params', 'modal', 'options', 'left'], props)}
    />
  );
};

export const ModalComponent = connect(null, {
  closeModal,
})(enhance(Component));

export default ModalComponent;
